import React, { FC, memo, MouseEvent, ReactNode, useCallback } from 'react';
import styled from 'styled-components';
import { StrictUnion } from '../../../../types';
import * as DataTableTypes from '../../utils/DataTableTypes';
import { SystemIconButton } from '../../../Icon/SystemIconButton';
import { DATA_TABLE_TOOLBAR_HEIGHT_PX } from '../../utils/DataTableHeights';
import { useDataTableContext } from '../../utils/DataTableContext';
import { noneBorderStyles, defaultBorderBottomStyles } from '../../utils/DataTableStyledElements';
import { SearchInputProps } from '../DataTableSearch/Search';
import { Message } from '../Message';
import { ColumnsConfiguration } from '../ColumnsConfiguration';
import { DataTableActiveFilters } from '../DataTableFilters/DataTableActiveFilters';
import { DataTableFilterIcon } from '../DataTableFilters/DataTableFilterIcon';
import { DataTableSearch } from '../DataTableSearch/DataTableSearch';
import { PluginDestination, usePluginContext } from '../../plugins/impl';
import { useIntlUds } from '../../../../translations/UdsIntlProvider';
import { useFetchingState } from '../DataTableQuickReports/utils/useFetchingState';
import { Skeleton } from '../../../Skeleton';
import { useDataTableQuickReportsContext } from '../../utils/DataTableQuickReportsContext';
import { QuickReportHasBeenModifiedWarning } from '../DataTableQuickReports/Toolbar/QuickReportHasBeenModifiedWarning';
import { BasePlugin } from '../../plugins';

interface BaseProps
  extends Omit<React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement>, 'css' | 'ref'> {
  message?: string;
  dataTest?: string;
  filterProps?: { dataTest?: string };
}

interface DataTableToolbarProps extends BaseProps {
  searchProps?: SearchInputProps;
  afterActions?: ReactNode | ReactNode[];
  beforeActions?: ReactNode | ReactNode[];
  addButtonProps?: {
    addDataTest?: string;
    disabled?: boolean;
    onAdd?: (event: MouseEvent<HTMLButtonElement>) => void;
    addLabel?: string;
  };
  children?: undefined;
}

interface DataTableToolbarPropsWithChildren extends BaseProps {
  children: ReactNode;
  hasSearch?: boolean;
  fullOverwriteChildren?: boolean;
}

type Props = StrictUnion<DataTableToolbarProps | DataTableToolbarPropsWithChildren>;

export const DataTableToolbarComponent: FC<React.PropsWithChildren<Props>> = ({
  message,
  searchProps,
  addButtonProps,
  dataTest = 'data-table-toolbar',
  filterProps,
  afterActions,
  beforeActions,
  children,
  fullOverwriteChildren,
  hasSearch: hasSearchProp,
  ...props
}) => {
  const { border } = useDataTableContext();
  const { unSavedQuickReport } = useDataTableQuickReportsContext();
  const isSecondaryToolbarVisible = usePluginContext().isDestinationDefined('toolbar', 'below');
  const { intl } = useIntlUds();
  const { isPageLoading } = useFetchingState();

  const skeleton = <Skeleton width={200} height={18} inline variant="rect" />;

  const hasSearch = !!searchProps;
  const handlePropClearSearch = useCallback(
    () => (!!searchProps?.onValueChange ? () => searchProps?.onValueChange?.('') : undefined),
    [searchProps]
  );

  return (
    <BasePlugin
      id="toolbar"
      key="toolbar"
      toolbar={{
        Component: (
          <React.Fragment key="toolbar">
            {!!children && fullOverwriteChildren && (
              <React.Fragment key="toolbar-full-children">{children}</React.Fragment>
            )}
            {!!children && !fullOverwriteChildren && (
              <StyledDataTableToolbarWrapper key="toolbar-children" $border={border}>
                <StyledDataTableToolbar {...props} data-test={dataTest}>
                  <StyledToolbarContent>
                    {isPageLoading ? (
                      skeleton
                    ) : (
                      <>
                        <Message message={message} />
                        {isSecondaryToolbarVisible && !!unSavedQuickReport && <StyledUnsavedQuickReportWarning />}
                        {!isSecondaryToolbarVisible && (
                          <DataTableActiveFilters
                            onSearchClear={handlePropClearSearch}
                            hasSearch={hasSearch || !!hasSearchProp}
                            dataTest={filterProps?.dataTest}
                          />
                        )}
                      </>
                    )}

                    <StyledRightAligned>
                      <PluginDestination name="toolbar" variant="middle">
                        {children}
                      </PluginDestination>
                    </StyledRightAligned>
                  </StyledToolbarContent>
                </StyledDataTableToolbar>
                {isSecondaryToolbarVisible && (
                  <PluginDestination name="toolbar" variant="below" wrapper={PluginWrapper} />
                )}
              </StyledDataTableToolbarWrapper>
            )}
            {!children && (
              <StyledDataTableToolbarWrapper key="toolbar" $border={border}>
                <StyledDataTableToolbar {...props} data-test={dataTest}>
                  <StyledToolbarContent>
                    {isPageLoading ? (
                      skeleton
                    ) : (
                      <>
                        <Message message={message} />
                        {isSecondaryToolbarVisible && !!unSavedQuickReport && <StyledUnsavedQuickReportWarning />}
                        {!isSecondaryToolbarVisible && (
                          <DataTableActiveFilters
                            onSearchClear={handlePropClearSearch}
                            hasSearch={hasSearch}
                            dataTest={filterProps?.dataTest}
                          />
                        )}
                      </>
                    )}

                    <StyledRightAligned>
                      <PluginDestination name="toolbar" variant="middle">
                        {searchProps && <DataTableSearch {...searchProps} />}
                        {beforeActions}
                        {addButtonProps?.onAdd && (
                          <SystemIconButton
                            dataTest={addButtonProps.addDataTest || 'data-table-add-button'}
                            aria-label={addButtonProps.addLabel || intl.DataTable.Toolbar.addButton.tooltipText}
                            tooltipText={addButtonProps.addLabel || intl.DataTable.Toolbar.addButton.tooltipText}
                            iconName="add"
                            disabled={addButtonProps.disabled}
                            onClick={addButtonProps.onAdd}
                          />
                        )}
                        {!isSecondaryToolbarVisible && [
                          <ColumnsConfiguration key="ColumnsConfiguration" />,
                          <DataTableFilterIcon key="DataTableFilterIcon" />
                        ]}
                        {afterActions}
                      </PluginDestination>
                    </StyledRightAligned>
                  </StyledToolbarContent>
                </StyledDataTableToolbar>
                {isSecondaryToolbarVisible && (
                  <PluginDestination name="toolbar" variant="below" wrapper={PluginWrapper} />
                )}
              </StyledDataTableToolbarWrapper>
            )}
          </React.Fragment>
        )
      }}
    />
  );
};

export const DataTableToolbar = memo(DataTableToolbarComponent);

const StyledRightAligned = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  flex-grow: 1;
`;

const StyledToolbarContent = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
`;

const StyledDataTableToolbar = styled.div<{ $height?: string; $padding?: string }>`
  position: relative;
  display: flex;
  align-items: center;
  height: ${({ $height }) => $height ?? DATA_TABLE_TOOLBAR_HEIGHT_PX};
  padding: ${({ $padding }) => $padding ?? '24px'};

  overflow-x: auto;
  overflow-y: hidden;
`;

const StyledDataTableToolbarWrapper = styled.div<{ $border?: DataTableTypes.DataTableBorder }>`
  ${StyledDataTableToolbar}:last-child {
    ${({ $border }) => ($border === 'none' ? noneBorderStyles : defaultBorderBottomStyles)};
  }
`;

const PluginWrapper = ({ children }: React.PropsWithChildren<{}>) => (
  <StyledDataTableToolbar $height="auto" $padding="8px" key="PluginWrapper">
    {children}
  </StyledDataTableToolbar>
);

const StyledUnsavedQuickReportWarning = styled(QuickReportHasBeenModifiedWarning)`
  margin-left: 8px;
  height: 36px;
  padding: 3px 10px;

  .MuiButton-root {
    padding: 3px 16px;
  }
  .MuiIconButton-root {
    padding: 5px;
  }
`;
